import {useCallback} from 'react';

import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {
    getSectionContent,
    removedSectionContent,
    uploadSectionContent,
} from 'src/openapi/admin/apis/SectionsContentApi';
import {useAppDispatch} from 'src/store/hooks';

import type {SuccessPayload} from 'src/typedef';

type Props = {
    sectionId: number,
};

type SectionContent = {
    name: string,
    file: File | null,
};

export const useEditSectionContent = ({sectionId}: Props) => {
    const apiDispatch = useAppDispatch();

    const getDispatchAction = useCallback(async (signal: AbortSignal) => {
        const response = await apiDispatch(
            getSectionContent({sectionId}, {signal}),
        );

        if (response.payload.success) {
            return Promise.resolve({type: '', payload: {
                success: true,
                error: false,
                data: {
                    name: response.payload.data.name || '',
                    file: null,
                },
            } as SuccessPayload<SectionContent>});
        } else {
            return Promise.resolve({type: '', payload: response.payload});
        }
    }, [sectionId, apiDispatch]);

    const saveDispatchAction = useCallback((data: SectionContent, signal: AbortSignal) => {
        if (data.file) {
            return apiDispatch(
                uploadSectionContent({sectionId, multipartFile: data.file}, {signal}),
            );
        } else {
            return apiDispatch(removedSectionContent({sectionId}, {signal}));
        }
    }, [apiDispatch, sectionId]);

    return useCommonDataHook({
        initValue: {
            name: '',
            file: null,
        },
        getDispatchAction,
        saveDispatchAction,
        shouldLoadData: false,
    });
};
